import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { settingsAPI, usersAPI, authAPI, whatsappAPI, clientsAPI, chatAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Building2,
  Mail,
  Phone,
  Users,
  Shield,
  Save,
  Plus,
  MoreHorizontal,
  Trash2,
  Euro,
  Loader2,
  Bell,
  BellOff,
  BellRing,
  CheckCircle2,
  XCircle,
  Smartphone,
  Wifi,
  WifiOff,
  RefreshCw,
  Link,
  Unlink,
  List,
  Pencil,
  GripVertical,
  ShieldCheck,
  KeyRound,
} from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const COLOR_PALETTE = [
  { key: 'verde',   label: 'Verde',   cls: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' },
  { key: 'blu',     label: 'Blu',     cls: 'bg-blue-500',    badge: 'bg-blue-100 text-blue-700' },
  { key: 'viola',   label: 'Viola',   cls: 'bg-purple-500',  badge: 'bg-purple-100 text-purple-700' },
  { key: 'arancio', label: 'Arancio', cls: 'bg-orange-500',  badge: 'bg-orange-100 text-orange-700' },
  { key: 'azzurro', label: 'Azzurro', cls: 'bg-cyan-500',    badge: 'bg-cyan-100 text-cyan-700' },
  { key: 'grigio',  label: 'Grigio',  cls: 'bg-slate-500',   badge: 'bg-slate-100 text-slate-700' },
  { key: 'rosso',   label: 'Rosso',   cls: 'bg-red-500',     badge: 'bg-red-100 text-red-700' },
  { key: 'giallo',  label: 'Giallo',  cls: 'bg-amber-500',   badge: 'bg-amber-100 text-amber-700' },
  { key: 'rosa',    label: 'Rosa',    cls: 'bg-rose-500',     badge: 'bg-rose-100 text-rose-700' },
  { key: 'indaco',  label: 'Indaco',  cls: 'bg-indigo-500',  badge: 'bg-indigo-100 text-indigo-700' },
];
const getBadgeClass = (colore) => COLOR_PALETTE.find(c => c.key === colore)?.badge || 'bg-slate-100 text-slate-700';
const getDotClass   = (colore) => COLOR_PALETTE.find(c => c.key === colore)?.cls  || 'bg-slate-400';

const DEFAULT_TIPI_CONTRATTO = [
  { valore: 'seo',        etichetta: 'SEO',        colore: 'verde' },
  { valore: 'assistenza', etichetta: 'Assistenza', colore: 'blu' },
  { valore: 'consulenza', etichetta: 'Consulenza', colore: 'viola' },
  { valore: 'dominio',    etichetta: 'Dominio',    colore: 'arancio' },
  { valore: 'hosting',    etichetta: 'Hosting',    colore: 'azzurro' },
  { valore: 'altro',      etichetta: 'Altro',      colore: 'grigio' },
];

const agencySchema = z.object({
  agenzia_nome: z.string().optional(),
  agenzia_indirizzo: z.string().optional(),
  agenzia_partita_iva: z.string().optional(),
  agenzia_email: z.string().email().optional().or(z.literal('')),
  agenzia_telefono: z.string().optional(),
  costo_orario_default: z.coerce.number().min(0).default(50),
});

const emailSchema = z.object({
  smtp_host: z.string().optional(),
  smtp_port: z.coerce.number().optional(),
  smtp_user: z.string().optional(),
  smtp_password: z.string().optional(),
  from_email: z.string().email().optional().or(z.literal('')),
  from_name: z.string().optional(),
  enabled: z.boolean().default(false),
});

const userSchema = z.object({
  nome: z.string().min(2, 'Nome richiesto'),
  cognome: z.string().min(2, 'Cognome richiesto'),
  email: z.string().email('Email non valida'),
  password: z.string().min(6, 'Minimo 6 caratteri'),
  ruolo: z.enum(['admin', 'collaboratore']),
});

const selfPasswordSchema = z.object({
  current_password: z.string().min(1, 'Campo obbligatorio'),
  new_password: z.string().min(6, 'Minimo 6 caratteri'),
  confirm_password: z.string().min(6, 'Minimo 6 caratteri'),
}).refine(d => d.new_password === d.confirm_password, {
  message: 'Le password non coincidono',
  path: ['confirm_password'],
});

const adminPasswordSchema = z.object({
  new_password: z.string().min(6, 'Minimo 6 caratteri'),
  confirm_password: z.string().min(6, 'Minimo 6 caratteri'),
}).refine(d => d.new_password === d.confirm_password, {
  message: 'Le password non coincidono',
  path: ['confirm_password'],
});

const ALL_SECTIONS = [
  { key: 'dashboard',    label: 'Dashboard' },
  { key: 'clienti',      label: 'Clienti' },
  { key: 'preventivi',   label: 'Preventivi' },
  { key: 'fatturazione', label: 'Fatturazione' },
  { key: 'scadenziario', label: 'Scadenziario' },
  { key: 'progetti',     label: 'Progetti' },
  { key: 'contratti',    label: 'Contratti' },
  { key: 'domini',       label: 'Domini' },
  { key: 'chat',         label: 'Chat' },
  { key: 'calendario',   label: 'Calendario' },
  { key: 'servizi',      label: 'Servizi' },
  { key: 'report',       label: 'Report' },
  { key: 'impostazioni', label: 'Impostazioni' },
];

export const Impostazioni = () => {
  const { user, isAdmin } = useAuth();
  const { permission, subscribed, requesting, supported, requestPermission, unsubscribe } = usePushNotifications();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifyEmailAssignment, setNotifyEmailAssignment] = useState(false);
  const [savingEmailPref, setSavingEmailPref] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [permissionsSelected, setPermissionsSelected] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [adminPasswordDialog, setAdminPasswordDialog] = useState(false);
  const [adminPasswordUser, setAdminPasswordUser] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // Voci elenco state
  const [tipiContratto, setTipiContratto] = useState(DEFAULT_TIPI_CONTRATTO);
  const [voceForm, setVoceForm] = useState({ etichetta: '', colore: 'grigio' });
  const [editingVoce, setEditingVoce] = useState(null);
  const [savingVoci, setSavingVoci] = useState(false);

  // WhatsApp state
  const [waBridgeStatus, setWaBridgeStatus] = useState(null);
  const [waQR, setWaQR] = useState(null);
  const [waMappings, setWaMappings] = useState([]);
  const [waClients, setWaClients] = useState([]);
  const [waConversations, setWaConversations] = useState([]);
  const [waRecentSenders, setWaRecentSenders] = useState([]);
  const [waGroups, setWaGroups] = useState([]);
  const [waGroupsLoading, setWaGroupsLoading] = useState(false);
  const [waPolling, setWaPolling] = useState(false);
  const [waMappingDialog, setWaMappingDialog] = useState(false);
  const [waEditMapping, setWaEditMapping] = useState(null);
  const [waForm, setWaForm] = useState({ wa_id: '', wa_name: '', client_id: '', conversation_id: '' });

  const agencyForm = useForm({
    resolver: zodResolver(agencySchema),
  });

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
  });

  const userForm = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      ruolo: 'collaboratore',
    },
  });

  const selfPasswordForm = useForm({
    resolver: zodResolver(selfPasswordSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  const adminPasswordForm = useForm({
    resolver: zodResolver(adminPasswordSchema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  useEffect(() => {
    fetchData();
  }, []);

  // WhatsApp data fetch
  const fetchWaData = async () => {
    try {
      const [statusRes, mappingsRes, clientsRes, convsRes] = await Promise.all([
        whatsappAPI.getBridgeStatus(),
        whatsappAPI.getMappings(),
        clientsAPI.getAll(),
        chatAPI.getConversations(),
      ]);
      setWaBridgeStatus(statusRes.data);
      setWaMappings(mappingsRes.data);
      setWaClients(clientsRes.data);
      setWaConversations(convsRes.data.filter(c => c.contesto !== 'whatsapp'));
      // Recent senders (only if bridge is reachable)
      if (statusRes.data.connected || statusRes.data.qr_available) {
        try {
          const recentRes = await whatsappAPI.getRecentSenders();
          setWaRecentSenders(recentRes.data);
        } catch {}
      }
    } catch {}
  };

  // Poll QR until connected
  useEffect(() => {
    if (!waPolling) return;
    const interval = setInterval(async () => {
      try {
        const statusRes = await whatsappAPI.getBridgeStatus();
        setWaBridgeStatus(statusRes.data);
        if (statusRes.data.connected) {
          setWaQR(null);
          setWaPolling(false);
          return;
        }
        if (statusRes.data.qr_available) {
          const qrRes = await whatsappAPI.getQR();
          setWaQR(qrRes.data.qr);
        }
      } catch {}
    }, 2500);
    return () => clearInterval(interval);
  }, [waPolling]);

  const handleWaRefresh = async () => {
    setWaPolling(true);
    await fetchWaData();
    if (!waBridgeStatus?.connected && waBridgeStatus?.qr_available) {
      try { const r = await whatsappAPI.getQR(); setWaQR(r.data.qr); } catch {}
    }
  };

  const handleLoadGroups = async () => {
    setWaGroupsLoading(true);
    try {
      const res = await whatsappAPI.getGroups();
      setWaGroups(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile caricare i gruppi');
    } finally {
      setWaGroupsLoading(false);
    }
  };

  const handleWaLogout = async () => {
    if (!window.confirm('Disconnettere WhatsApp?')) return;
    try { await whatsappAPI.logout(); await fetchWaData(); toast.success('WhatsApp disconnesso'); }
    catch { toast.error('Errore disconnessione'); }
  };

  const handleSaveWaMapping = async () => {
    if (!waForm.wa_id.trim() || !waForm.wa_name.trim()) {
      toast.error('ID e nome obbligatori'); return;
    }
    try {
      if (waEditMapping) {
        await whatsappAPI.updateMapping(waEditMapping.id, waForm);
      } else {
        await whatsappAPI.createMapping(waForm);
      }
      toast.success('Contatto salvato');
      setWaMappingDialog(false);
      setWaEditMapping(null);
      setWaForm({ wa_id: '', wa_name: '', client_id: '', conversation_id: '' });
      fetchWaData();
    } catch { toast.error('Errore salvataggio'); }
  };

  const handleDeleteWaMapping = async (id) => {
    if (!window.confirm('Rimuovere questo contatto?')) return;
    try { await whatsappAPI.deleteMapping(id); fetchWaData(); toast.success('Rimosso'); }
    catch { toast.error('Errore'); }
  };

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes] = await Promise.all([
        settingsAPI.get(),
        usersAPI.getAll(),
      ]);
      setSettings(settingsRes.data);
      setUsers(usersRes.data);
      const lista = settingsRes.data?.voci_elenco?.tipi_contratto;
      if (lista?.length) setTipiContratto(lista);

      setNotifyEmailAssignment(!!usersRes.data.find(u => u.id === user?.id)?.notify_email_assignment);

      // Set form values
      agencyForm.reset({
        agenzia_nome: settingsRes.data.agenzia_nome || '',
        agenzia_indirizzo: settingsRes.data.agenzia_indirizzo || '',
        agenzia_partita_iva: settingsRes.data.agenzia_partita_iva || '',
        agenzia_email: settingsRes.data.agenzia_email || '',
        agenzia_telefono: settingsRes.data.agenzia_telefono || '',
        costo_orario_default: settingsRes.data.costo_orario_default || 50,
      });
      
      emailForm.reset({
        smtp_host: settingsRes.data.email?.smtp_host || '',
        smtp_port: settingsRes.data.email?.smtp_port || 587,
        smtp_user: settingsRes.data.email?.smtp_user || '',
        smtp_password: settingsRes.data.email?.smtp_password || '',
        from_email: settingsRes.data.email?.from_email || '',
        from_name: settingsRes.data.email?.from_name || '',
        enabled: settingsRes.data.email?.enabled || false,
      });
    } catch (error) {
      toast.error('Errore nel caricamento impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const onSaveAgency = async (data) => {
    setSaving(true);
    try {
      await settingsAPI.update({
        ...settings,
        ...data,
      });
      toast.success('Impostazioni salvate');
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const onSaveEmail = async (data) => {
    setSaving(true);
    try {
      await settingsAPI.update({
        ...settings,
        email: data,
      });
      toast.success('Configurazione email salvata');
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEmailPref = async (value) => {
    setNotifyEmailAssignment(value);
    setSavingEmailPref(true);
    try {
      await usersAPI.updatePreferences({ notify_email_assignment: value });
      toast.success(value ? 'Notifiche email attivate' : 'Notifiche email disattivate');
    } catch {
      toast.error('Errore nel salvataggio');
      setNotifyEmailAssignment(!value);
    } finally {
      setSavingEmailPref(false);
    }
  };

  const saveVociElenco = async (lista) => {
    setSavingVoci(true);
    try {
      await settingsAPI.update({
        ...settings,
        voci_elenco: { ...(settings?.voci_elenco || {}), tipi_contratto: lista },
      });
      setTipiContratto(lista);
      setSettings(s => ({ ...s, voci_elenco: { ...(s?.voci_elenco || {}), tipi_contratto: lista } }));
      toast.success('Voci salvate');
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingVoci(false);
    }
  };

  const handleAddVoce = async () => {
    if (!voceForm.etichetta.trim()) { toast.error('Etichetta richiesta'); return; }
    const valore = voceForm.etichetta.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (tipiContratto.some(t => t.valore === valore)) { toast.error('Valore già esistente'); return; }
    const nuova = [...tipiContratto, { valore, etichetta: voceForm.etichetta.trim(), colore: voceForm.colore }];
    await saveVociElenco(nuova);
    setVoceForm({ etichetta: '', colore: 'grigio' });
  };

  const handleUpdateVoce = async (idx) => {
    if (!editingVoce?.etichetta?.trim()) { toast.error('Etichetta richiesta'); return; }
    const nuova = tipiContratto.map((t, i) => i === idx ? { ...t, etichetta: editingVoce.etichetta.trim(), colore: editingVoce.colore } : t);
    await saveVociElenco(nuova);
    setEditingVoce(null);
  };

  const handleDeleteVoce = async (idx) => {
    if (!window.confirm('Eliminare questa voce?')) return;
    await saveVociElenco(tipiContratto.filter((_, i) => i !== idx));
  };

  const onCreateUser = async (data) => {
    try {
      await authAPI.register(data);
      toast.success('Utente creato');
      fetchData();
      setUserDialogOpen(false);
      userForm.reset({ ruolo: 'collaboratore' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleOpenPermissions = (u) => {
    setPermissionsUser(u);
    setPermissionsSelected(u.permissions && u.permissions.length > 0 ? [...u.permissions] : ALL_SECTIONS.map(s => s.key));
    setPermissionsDialog(true);
  };

  const handleSavePermissions = async () => {
    setSavingPermissions(true);
    try {
      const allSelected = permissionsSelected.length === ALL_SECTIONS.length;
      await usersAPI.updatePermissions(permissionsUser.id, allSelected ? null : permissionsSelected);
      toast.success('Permessi aggiornati');
      setPermissionsDialog(false);
      fetchData();
    } catch {
      toast.error('Errore nel salvataggio dei permessi');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    try {
      await usersAPI.delete(userId);
      toast.success('Utente eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleOpenAdminPassword = (u) => {
    setAdminPasswordUser(u);
    adminPasswordForm.reset({ new_password: '', confirm_password: '' });
    setAdminPasswordDialog(true);
  };

  const handleAdminPassword = async (data) => {
    setChangingPassword(true);
    try {
      await usersAPI.changePassword(adminPasswordUser.id, { new_password: data.new_password });
      toast.success('Password aggiornata');
      setAdminPasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSelfPassword = async (data) => {
    setChangingPassword(true);
    try {
      await usersAPI.changeMyPassword({ current_password: data.current_password, new_password: data.new_password });
      toast.success('Password aggiornata con successo');
      selfPasswordForm.reset();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Impostazioni">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Impostazioni">
      <div className="max-w-4xl mx-auto">
        <Tabs defaultValue="agency" className="space-y-6">
          <TabsList className={`grid w-full ${isAdmin() ? 'grid-cols-7' : 'grid-cols-4'}`}>
            <TabsTrigger value="agency" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" /> <span className="hidden sm:inline">Agenzia</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" /> <span className="hidden sm:inline">Email</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" /> <span className="hidden sm:inline">Notifiche</span>
            </TabsTrigger>
            <TabsTrigger value="sicurezza" className="flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> <span className="hidden sm:inline">Sicurezza</span>
            </TabsTrigger>
            {isAdmin() && (
              <TabsTrigger value="voci" className="flex items-center gap-2">
                <List className="h-4 w-4" /> <span className="hidden sm:inline">Voci</span>
              </TabsTrigger>
            )}
            {isAdmin() && (
              <TabsTrigger value="whatsapp" className="flex items-center gap-2" onClick={fetchWaData}>
                <Smartphone className="h-4 w-4" /> <span className="hidden sm:inline">WhatsApp</span>
              </TabsTrigger>
            )}
            {isAdmin() && (
              <TabsTrigger value="users" className="flex items-center gap-2">
                <Users className="h-4 w-4" /> <span className="hidden sm:inline">Utenti</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Agency Settings */}
          <TabsContent value="agency">
            <Card data-testid="agency-settings">
              <CardHeader>
                <CardTitle>Dati Agenzia</CardTitle>
                <CardDescription>Configura i dati della tua agenzia</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={agencyForm.handleSubmit(onSaveAgency)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome Agenzia</Label>
                      <Input {...agencyForm.register('agenzia_nome')} placeholder="La Mia Agenzia" data-testid="agency-nome-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Partita IVA</Label>
                      <Input {...agencyForm.register('agenzia_partita_iva')} placeholder="IT01234567890" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Indirizzo</Label>
                    <Input {...agencyForm.register('agenzia_indirizzo')} placeholder="Via Roma 1, 00100 Roma" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email Agenzia</Label>
                      <Input {...agencyForm.register('agenzia_email')} type="email" placeholder="info@miagenzia.it" />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefono</Label>
                      <Input {...agencyForm.register('agenzia_telefono')} placeholder="+39 06 1234567" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Logo Agenzia</Label>
                    <div className="flex items-center gap-4">
                      {settings?.agenzia_logo && (
                        <img
                          src={settings.agenzia_logo}
                          alt="Logo agenzia"
                          className="h-16 w-auto object-contain rounded border border-slate-200 p-1"
                        />
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          id="logo-upload"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = async (ev) => {
                              const base64 = ev.target.result;
                              try {
                                await settingsAPI.update({ ...settings, agenzia_logo: base64 });
                                toast.success('Logo aggiornato');
                                fetchData();
                              } catch {
                                toast.error('Errore nel caricamento logo');
                              }
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                        <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('logo-upload').click()}>
                          {settings?.agenzia_logo ? 'Cambia logo' : 'Carica logo'}
                        </Button>
                        {settings?.agenzia_logo && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="ml-2 text-red-500 hover:text-red-700"
                            onClick={async () => {
                              try {
                                await settingsAPI.update({ ...settings, agenzia_logo: null });
                                toast.success('Logo rimosso');
                                fetchData();
                              } catch {
                                toast.error('Errore nella rimozione logo');
                              }
                            }}
                          >
                            Rimuovi
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">PNG, JPG o SVG. Verrà usato nei documenti e nell'header.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Euro className="h-4 w-4" /> Costo Orario Default
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      {...agencyForm.register('costo_orario_default')}
                      className="max-w-xs"
                      data-testid="agency-costo-input"
                    />
                    <p className="text-xs text-slate-500">
                      Questo valore verrà usato come default per le nuove registrazioni ore
                    </p>
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="save-agency-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Salva
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Settings */}
          <TabsContent value="email">
            <Card data-testid="email-settings">
              <CardHeader>
                <CardTitle>Configurazione Email</CardTitle>
                <CardDescription>
                  Configura il server SMTP per ricevere notifiche via email
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={emailForm.handleSubmit(onSaveEmail)} className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Abilita notifiche email</p>
                      <p className="text-sm text-slate-500">Ricevi alert per scadenze e rinnovi</p>
                    </div>
                    <Controller
                      name="enabled"
                      control={emailForm.control}
                      render={({ field }) => (
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Server SMTP</Label>
                      <Input {...emailForm.register('smtp_host')} placeholder="smtp.example.com" data-testid="email-host-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Porta</Label>
                      <Input type="number" {...emailForm.register('smtp_port')} placeholder="587" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input {...emailForm.register('smtp_user')} placeholder="user@example.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input type="password" {...emailForm.register('smtp_password')} placeholder="••••••••" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email Mittente</Label>
                      <Input {...emailForm.register('from_email')} placeholder="noreply@agenzia.it" />
                    </div>
                    <div className="space-y-2">
                      <Label>Nome Mittente</Label>
                      <Input {...emailForm.register('from_name')} placeholder="AgencyOS" />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="save-email-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Salva
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Push Notifications */}
          <TabsContent value="notifications">
            <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Notifiche via Email</CardTitle>
                <CardDescription>
                  Ricevi un'email quando ti viene assegnato un progetto o un'attività
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-slate-600" />
                    <div>
                      <p className="font-medium text-sm">Assegnazione progetti e attività</p>
                      <p className="text-xs text-slate-500">Ricevi un'email quando vieni aggiunto a un progetto o ti viene assegnata un'attività</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifyEmailAssignment}
                    onCheckedChange={handleToggleEmailPref}
                    disabled={savingEmailPref}
                  />
                </div>
                {!settings?.email?.enabled && (
                  <p className="text-xs text-amber-600 mt-3 flex items-center gap-1">
                    <span>⚠</span> Le notifiche email richiedono la configurazione SMTP nella tab <strong>Email</strong>.
                  </p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Notifiche Push</CardTitle>
                <CardDescription>
                  Ricevi notifiche sul tuo dispositivo quando arrivano nuovi messaggi in chat
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!supported ? (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <XCircle className="h-5 w-5 text-amber-600 shrink-0" />
                    <p className="text-sm text-amber-800">
                      Il tuo browser non supporta le notifiche push. Prova ad aprire l'app da Chrome o Safari su mobile.
                    </p>
                  </div>
                ) : permission === 'denied' ? (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <BellOff className="h-5 w-5 text-red-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Notifiche bloccate</p>
                      <p className="text-sm text-red-700 mt-1">
                        Hai bloccato le notifiche per questo sito. Per abilitarle, vai nelle impostazioni del browser e consenti le notifiche per questo sito.
                      </p>
                    </div>
                  </div>
                ) : subscribed ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-emerald-800">Notifiche push attive</p>
                        <p className="text-sm text-emerald-700 mt-0.5">
                          Riceverai notifiche per nuovi messaggi in chat anche quando l'app è in background.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <BellRing className="h-5 w-5 text-slate-600" />
                        <div>
                          <p className="font-medium text-sm">Notifiche chat</p>
                          <p className="text-xs text-slate-500">Nuovi messaggi in arrivo</p>
                        </div>
                      </div>
                      <Badge className="bg-emerald-100 text-emerald-700">Attive</Badge>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={unsubscribe}
                      >
                        <BellOff className="h-4 w-4 mr-2" /> Disattiva notifiche
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <Bell className="h-5 w-5 text-slate-500 shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-700">Notifiche push non attive</p>
                        <p className="text-sm text-slate-500 mt-0.5">
                          Attiva le notifiche per sapere subito quando arriva un nuovo messaggio in chat.
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        className="bg-indigo-600 hover:bg-indigo-700"
                        disabled={requesting}
                        onClick={requestPermission}
                      >
                        {requesting ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Bell className="h-4 w-4 mr-2" />
                        )}
                        {requesting ? 'Attivazione…' : 'Attiva notifiche push'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </TabsContent>

          {/* Sicurezza — cambio password */}
          <TabsContent value="sicurezza">
            <Card>
              <CardHeader>
                <CardTitle>Cambia Password</CardTitle>
                <CardDescription>Aggiorna la tua password di accesso</CardDescription>
              </CardHeader>
              <CardContent className="max-w-md">
                <form onSubmit={selfPasswordForm.handleSubmit(handleSelfPassword)} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Password attuale *</Label>
                    <Input type="password" {...selfPasswordForm.register('current_password')} />
                    {selfPasswordForm.formState.errors.current_password && (
                      <p className="text-sm text-red-500">{selfPasswordForm.formState.errors.current_password.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Nuova password *</Label>
                    <Input type="password" {...selfPasswordForm.register('new_password')} />
                    {selfPasswordForm.formState.errors.new_password && (
                      <p className="text-sm text-red-500">{selfPasswordForm.formState.errors.new_password.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Conferma nuova password *</Label>
                    <Input type="password" {...selfPasswordForm.register('confirm_password')} />
                    {selfPasswordForm.formState.errors.confirm_password && (
                      <p className="text-sm text-red-500">{selfPasswordForm.formState.errors.confirm_password.message}</p>
                    )}
                  </div>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={changingPassword}>
                    {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                    Aggiorna password
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Voci Elenco */}
          {isAdmin() && (
            <TabsContent value="voci">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><List className="h-5 w-5 text-indigo-600" /> Voci Elenco</CardTitle>
                  <CardDescription>Gestisci le voci dei menu a tendina usate nel sistema</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                  {/* Tipi Contratto */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Tipi Contratto</h3>
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Etichetta</TableHead>
                            <TableHead>Valore (chiave)</TableHead>
                            <TableHead>Colore</TableHead>
                            <TableHead className="w-20"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tipiContratto.map((t, idx) => (
                            <TableRow key={t.valore}>
                              {editingVoce?.idx === idx ? (
                                <>
                                  <TableCell>
                                    <Input
                                      className="h-8"
                                      value={editingVoce.etichetta}
                                      onChange={e => setEditingVoce(v => ({ ...v, etichetta: e.target.value }))}
                                      onKeyDown={e => e.key === 'Enter' && handleUpdateVoce(idx)}
                                      autoFocus
                                    />
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-400 font-mono">{t.valore}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                      {COLOR_PALETTE.map(c => (
                                        <button
                                          key={c.key}
                                          title={c.label}
                                          onClick={() => setEditingVoce(v => ({ ...v, colore: c.key }))}
                                          className={`w-5 h-5 rounded-full ${c.cls} border-2 ${editingVoce.colore === c.key ? 'border-slate-800' : 'border-transparent'}`}
                                        />
                                      ))}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      <Button size="sm" className="h-7 bg-indigo-600 hover:bg-indigo-700 px-2" onClick={() => handleUpdateVoce(idx)} disabled={savingVoci}>
                                        {savingVoci ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingVoce(null)}>✕</Button>
                                    </div>
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell>
                                    <Badge className={getBadgeClass(t.colore)}>{t.etichetta}</Badge>
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-400 font-mono">{t.valore}</TableCell>
                                  <TableCell>
                                    <span className={`inline-block w-4 h-4 rounded-full ${getDotClass(t.colore)}`} />
                                  </TableCell>
                                  <TableCell>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => setEditingVoce({ idx, etichetta: t.etichetta, colore: t.colore })}>
                                          <Pencil className="h-4 w-4 mr-2" /> Modifica
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteVoce(idx)}>
                                          <Trash2 className="h-4 w-4 mr-2" /> Elimina
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Add new */}
                    <div className="mt-3 flex items-end gap-3">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Nuova voce</Label>
                        <Input
                          placeholder="es. Manutenzione"
                          value={voceForm.etichetta}
                          onChange={e => setVoceForm(v => ({ ...v, etichetta: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && handleAddVoce()}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Colore</Label>
                        <div className="flex gap-1 flex-wrap w-40">
                          {COLOR_PALETTE.map(c => (
                            <button
                              key={c.key}
                              title={c.label}
                              onClick={() => setVoceForm(v => ({ ...v, colore: c.key }))}
                              className={`w-6 h-6 rounded-full ${c.cls} border-2 ${voceForm.colore === c.key ? 'border-slate-800' : 'border-transparent'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <Button
                        className="bg-indigo-600 hover:bg-indigo-700 h-9"
                        onClick={handleAddVoce}
                        disabled={savingVoci}
                      >
                        {savingVoci ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                        Aggiungi
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">La chiave interna viene generata automaticamente dall'etichetta. I contratti esistenti non vengono modificati.</p>
                  </div>

                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* WhatsApp Integration */}
          {isAdmin() && (
            <TabsContent value="whatsapp">
              <div className="space-y-4">

                {/* Bridge Status Card */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Smartphone className="h-5 w-5 text-emerald-600" /> Connessione WhatsApp
                        </CardTitle>
                        <CardDescription>Collega il tuo WhatsApp al CRM tramite il bridge locale</CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleWaRefresh} className="gap-2">
                        <RefreshCw className="h-4 w-4" /> Aggiorna
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {!waBridgeStatus ? (
                      <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg text-slate-500 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" /> Clicca "Aggiorna" per controllare lo stato del bridge
                      </div>
                    ) : waBridgeStatus.error ? (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                          <WifiOff className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-red-800 text-sm">Bridge non raggiungibile</p>
                            <p className="text-xs text-red-600 mt-1">{waBridgeStatus.error}</p>
                          </div>
                        </div>
                        <div className="p-4 bg-slate-50 rounded-lg border text-sm space-y-2">
                          <p className="font-medium text-slate-700">Per avviare il bridge:</p>
                          <pre className="text-xs bg-slate-900 text-emerald-400 p-3 rounded-lg overflow-x-auto">{`cd whatsapp-bridge\nnpm install\nnode index.js`}</pre>
                        </div>
                      </div>
                    ) : waBridgeStatus.connected ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <Wifi className="h-5 w-5 text-emerald-600 shrink-0" />
                          <div className="flex-1">
                            <p className="font-medium text-emerald-800 text-sm">WhatsApp connesso</p>
                            {waBridgeStatus.display_name && (
                              <p className="text-xs text-emerald-700 mt-0.5">
                                {waBridgeStatus.display_name} (+{waBridgeStatus.phone})
                              </p>
                            )}
                          </div>
                          <Button variant="outline" size="sm" onClick={handleWaLogout}
                            className="text-red-600 border-red-200 hover:bg-red-50">
                            Disconnetti
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <WifiOff className="h-5 w-5 text-amber-600 shrink-0" />
                          <div>
                            <p className="font-medium text-amber-800 text-sm">WhatsApp non connesso</p>
                            <p className="text-xs text-amber-700 mt-0.5">Scansiona il QR code con WhatsApp sul tuo telefono</p>
                          </div>
                        </div>
                        {waQR ? (
                          <div className="flex flex-col items-center gap-3 py-4">
                            <img src={waQR} alt="QR Code WhatsApp" className="w-56 h-56 rounded-xl border border-slate-200 shadow" />
                            <p className="text-xs text-slate-500 text-center">
                              Apri WhatsApp → Dispositivi collegati → Collega un dispositivo → Scansiona
                            </p>
                            <p className="text-xs text-slate-400 animate-pulse">In attesa di scansione…</p>
                          </div>
                        ) : (
                          <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleWaRefresh}>
                            <Smartphone className="h-4 w-4 mr-2" /> Genera QR Code
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Groups */}
                {waBridgeStatus?.connected && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-base">Gruppi WhatsApp</CardTitle>
                        <CardDescription>Tutti i gruppi di cui fa parte il numero connesso</CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={handleLoadGroups} disabled={waGroupsLoading} className="gap-2">
                        {waGroupsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Carica gruppi
                      </Button>
                    </CardHeader>
                    {waGroups.length > 0 && (
                      <CardContent>
                        <div className="space-y-2">
                          {waGroups.map(g => {
                            const alreadyMapped = waMappings.some(m => m.wa_id === g.wa_id);
                            return (
                              <div key={g.wa_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-base shrink-0">👥</div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-800 truncate">{g.wa_name}</p>
                                  <p className="text-xs text-slate-400 font-mono truncate">{g.wa_id}</p>
                                  <p className="text-xs text-slate-400">{g.participants} partecipanti</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    className="text-xs text-slate-400 hover:text-slate-600 underline"
                                    onClick={() => { navigator.clipboard.writeText(g.wa_id); toast.success('ID copiato!'); }}
                                  >
                                    Copia ID
                                  </button>
                                  {alreadyMapped ? (
                                    <Badge className="bg-emerald-100 text-emerald-700">Collegato</Badge>
                                  ) : (
                                    <Button size="sm" variant="outline"
                                      onClick={() => {
                                        setWaEditMapping(null);
                                        setWaForm({ wa_id: g.wa_id, wa_name: g.wa_name, client_id: '', conversation_id: '' });
                                        setWaMappingDialog(true);
                                      }}>
                                      <Link className="h-3 w-3 mr-1" /> Aggiungi
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                )}

                {/* Recent Senders */}
                {waRecentSenders.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Contatti recenti rilevati</CardTitle>
                      <CardDescription>Questi contatti hanno inviato messaggi — clicca "Aggiungi" per collegarli al CRM</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {waRecentSenders.map(s => {
                          const alreadyMapped = waMappings.some(m => m.wa_id === s.wa_id);
                          return (
                            <div key={s.wa_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-base shrink-0">
                                {s.is_group ? '👥' : '👤'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{s.wa_name}</p>
                                <p className="text-xs text-slate-400 font-mono truncate">{s.wa_id}</p>
                                <p className="text-xs text-slate-500 truncate mt-0.5 italic">"{s.last_message}"</p>
                              </div>
                              {alreadyMapped ? (
                                <Badge className="bg-emerald-100 text-emerald-700 shrink-0">Collegato</Badge>
                              ) : (
                                <Button size="sm" variant="outline" className="shrink-0"
                                  onClick={() => {
                                    setWaEditMapping(null);
                                    setWaForm({ wa_id: s.wa_id, wa_name: s.wa_name, client_id: '', conversation_id: '' });
                                    setWaMappingDialog(true);
                                  }}>
                                  <Link className="h-3 w-3 mr-1" /> Aggiungi
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Contacts Mapping */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Contatti Monitorati</CardTitle>
                      <CardDescription>Associa i contatti WhatsApp ai clienti e alle conversazioni del CRM</CardDescription>
                    </div>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" size="sm"
                      onClick={() => { setWaEditMapping(null); setWaForm({ wa_id: '', wa_name: '', client_id: '', conversation_id: '' }); setWaMappingDialog(true); }}>
                      <Plus className="h-4 w-4 mr-1" /> Aggiungi
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {waMappings.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-sm">
                        <Smartphone className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p>Nessun contatto configurato.</p>
                        <p className="text-xs mt-1">I messaggi WhatsApp vengono comunque ricevuti e creano automaticamente conversazioni.</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contatto WhatsApp</TableHead>
                            <TableHead>Numero / ID</TableHead>
                            <TableHead>Cliente CRM</TableHead>
                            <TableHead className="w-16"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {waMappings.map(m => (
                            <TableRow key={m.id}>
                              <TableCell className="font-medium">{m.wa_name}</TableCell>
                              <TableCell className="text-slate-500 text-xs font-mono">{m.wa_id}</TableCell>
                              <TableCell>
                                {m.client_id
                                  ? <Badge className="bg-indigo-100 text-indigo-700">{waClients.find(c => c.id === m.client_id)?.nome || m.client_id}</Badge>
                                  : <span className="text-xs text-slate-400">—</span>}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => {
                                      setWaEditMapping(m);
                                      setWaForm({ wa_id: m.wa_id, wa_name: m.wa_name, client_id: m.client_id || '', conversation_id: m.conversation_id || '' });
                                      setWaMappingDialog(true);
                                    }}>
                                      <Link className="h-4 w-4 mr-2" /> Modifica
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteWaMapping(m.id)}>
                                      <Unlink className="h-4 w-4 mr-2" /> Rimuovi
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Mapping Dialog */}
              <Dialog open={waMappingDialog} onOpenChange={setWaMappingDialog}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>{waEditMapping ? 'Modifica contatto' : 'Aggiungi contatto WhatsApp'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div className="space-y-1.5">
                      <Label>ID WhatsApp *</Label>
                      <Input placeholder="es. 393331234567@c.us" value={waForm.wa_id}
                        onChange={e => setWaForm(p => ({ ...p, wa_id: e.target.value }))} />
                      <p className="text-xs text-slate-400">Prefisso internazionale senza + (es. 39 per Italia) + numero + @c.us. Per i gruppi: ID@g.us</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Nome visualizzato *</Label>
                      <Input placeholder="es. Mario Rossi" value={waForm.wa_name}
                        onChange={e => setWaForm(p => ({ ...p, wa_name: e.target.value }))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Cliente CRM (opzionale)</Label>
                      <select className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={waForm.client_id} onChange={e => setWaForm(p => ({ ...p, client_id: e.target.value }))}>
                        <option value="">Nessun cliente</option>
                        {waClients.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Conversazione CRM esistente (opzionale)</Label>
                      <select className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={waForm.conversation_id} onChange={e => setWaForm(p => ({ ...p, conversation_id: e.target.value }))}>
                        <option value="">Crea nuova automaticamente</option>
                        {waConversations.map(c => <option key={c.id} value={c.id}>{c.titolo}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button variant="outline" onClick={() => setWaMappingDialog(false)}>Annulla</Button>
                      <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveWaMapping}>Salva</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>
          )}

          {/* Users Management */}
          {isAdmin() && (
            <TabsContent value="users">
              <Card data-testid="users-settings">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Gestione Utenti</CardTitle>
                    <CardDescription>Aggiungi e gestisci gli utenti del team</CardDescription>
                  </div>
                  <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-user-btn">
                        <Plus className="h-4 w-4 mr-2" /> Nuovo Utente
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Nuovo Utente</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={userForm.handleSubmit(onCreateUser)} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Nome *</Label>
                            <Input {...userForm.register('nome')} data-testid="user-nome-input" />
                            {userForm.formState.errors.nome && (
                              <p className="text-sm text-red-500">{userForm.formState.errors.nome.message}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Cognome *</Label>
                            <Input {...userForm.register('cognome')} />
                            {userForm.formState.errors.cognome && (
                              <p className="text-sm text-red-500">{userForm.formState.errors.cognome.message}</p>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input type="email" {...userForm.register('email')} data-testid="user-email-input" />
                          {userForm.formState.errors.email && (
                            <p className="text-sm text-red-500">{userForm.formState.errors.email.message}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Password *</Label>
                          <Input type="password" {...userForm.register('password')} data-testid="user-password-input" />
                          {userForm.formState.errors.password && (
                            <p className="text-sm text-red-500">{userForm.formState.errors.password.message}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Ruolo *</Label>
                          <Controller
                            name="ruolo"
                            control={userForm.control}
                            render={({ field }) => (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Amministratore</SelectItem>
                                  <SelectItem value="collaboratore">Collaboratore</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                          <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>
                            Annulla
                          </Button>
                          <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-user-btn">
                            Crea
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Ruolo</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">
                            {u.nome} {u.cognome}
                            {u.id === user?.id && (
                              <Badge variant="outline" className="ml-2 text-xs">Tu</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500">{u.email}</TableCell>
                          <TableCell>
                            <Badge className={u.ruolo === 'admin' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'}>
                              {u.ruolo === 'admin' ? 'Admin' : 'Collaboratore'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {u.is_active ? 'Attivo' : 'Disabilitato'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {u.id !== user?.id && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {u.ruolo !== 'admin' && (
                                    <DropdownMenuItem onClick={() => handleOpenPermissions(u)}>
                                      <ShieldCheck className="h-4 w-4 mr-2" /> Permessi
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => handleOpenAdminPassword(u)}>
                                    <KeyRound className="h-4 w-4 mr-2" /> Cambia Password
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-red-600"
                                    onClick={() => handleDeleteUser(u.id)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" /> Elimina
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Dialog cambio password utente (admin) */}
              <Dialog open={adminPasswordDialog} onOpenChange={setAdminPasswordDialog}>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>
                      Cambia Password — {adminPasswordUser?.nome} {adminPasswordUser?.cognome}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={adminPasswordForm.handleSubmit(handleAdminPassword)} className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label>Nuova password *</Label>
                      <Input type="password" {...adminPasswordForm.register('new_password')} />
                      {adminPasswordForm.formState.errors.new_password && (
                        <p className="text-sm text-red-500">{adminPasswordForm.formState.errors.new_password.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Conferma password *</Label>
                      <Input type="password" {...adminPasswordForm.register('confirm_password')} />
                      {adminPasswordForm.formState.errors.confirm_password && (
                        <p className="text-sm text-red-500">{adminPasswordForm.formState.errors.confirm_password.message}</p>
                      )}
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setAdminPasswordDialog(false)}>Annulla</Button>
                      <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={changingPassword}>
                        {changingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Salva
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Dialog permessi utente */}
              <Dialog open={permissionsDialog} onOpenChange={setPermissionsDialog}>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>
                      Permessi — {permissionsUser?.nome} {permissionsUser?.cognome}
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-sm text-slate-500 mb-3">
                    Seleziona le sezioni visibili per questo utente.
                  </p>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {ALL_SECTIONS.map(s => (
                      <div key={s.key} className="flex items-center gap-3 py-1">
                        <Checkbox
                          id={`perm-${s.key}`}
                          checked={permissionsSelected.includes(s.key)}
                          onCheckedChange={(checked) =>
                            setPermissionsSelected(prev =>
                              checked ? [...prev, s.key] : prev.filter(k => k !== s.key)
                            )
                          }
                        />
                        <Label htmlFor={`perm-${s.key}`} className="cursor-pointer font-normal">{s.label}</Label>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-4 gap-3">
                    <button
                      type="button"
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => setPermissionsSelected(ALL_SECTIONS.map(s => s.key))}
                    >
                      Seleziona tutto
                    </button>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setPermissionsDialog(false)}>Annulla</Button>
                      <Button
                        className="bg-indigo-600 hover:bg-indigo-700"
                        onClick={handleSavePermissions}
                        disabled={savingPermissions}
                      >
                        {savingPermissions ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Salva
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
};
